/**
 * FretePorCepPage
 *
 * CRUD de faixas de tele-entrega por CEP (Configurações ERP).
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Truck,
  Plus,
  Pencil,
  Ban,
  Loader2,
  RotateCcw,
  Search,
  MapPin,
} from 'lucide-react'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import { useToast } from '../../../hooks/useToast'
import { Toast } from '../../../components'
import { shippingZonesService } from '../../../services/shippingZonesService'
import './ErpSettingsHubPage.css'
import './CockpitSettingsPage.css'
import './FretePorCepPage.css'

const EMPTY_FORM = {
  region: '',
  neighborhood: '',
  price: '',
  cepStart: '',
  cepEnd: '',
  notes: '',
  active: true,
  specialService: false,
}

function formatCepInput(value) {
  const d = String(value || '').replace(/\D/g, '').slice(0, 8)
  if (d.length <= 5) return d
  return `${d.slice(0, 5)}-${d.slice(5)}`
}

function formatCepDisplay(value) {
  if (!value) return '—'
  return formatCepInput(value)
}

function formatMoney(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0)
}

function zoneToForm(zone) {
  const special = !zone.cepStart && !zone.cepEnd
  return {
    region: zone.region || '',
    neighborhood: zone.neighborhood || '',
    price: String(zone.price ?? ''),
    cepStart: zone.cepStart ? formatCepInput(zone.cepStart) : '',
    cepEnd: zone.cepEnd ? formatCepInput(zone.cepEnd) : '',
    notes: zone.notes || '',
    active: zone.active !== false,
    specialService: special,
  }
}

function FretePorCepPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const { toast, showToast, hideToast } = useToast()

  const canEdit = hasPermission(MODULES.SETTINGS, ACTIONS.UPDATE)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [zones, setZones] = useState([])
  const [regions, setRegions] = useState([])
  const [search, setSearch] = useState('')
  const [regionFilter, setRegionFilter] = useState('')
  const [activeOnly, setActiveOnly] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)

  const [testCep, setTestCep] = useState('')
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState(null)

  const loadZones = useCallback(async () => {
    setLoading(true)
    try {
      const response = await shippingZonesService.list({
        search: search.trim() || undefined,
        region: regionFilter || undefined,
        activeOnly: activeOnly ? 'true' : undefined,
      })
      if (response?.success) {
        setZones(response.data?.zones || [])
        setRegions(response.data?.regions || [])
      } else {
        showToast(response?.message || 'Erro ao carregar faixas', 'error')
      }
    } catch (error) {
      console.error(error)
      showToast('Erro ao carregar faixas de frete', 'error')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, regionFilter, activeOnly])

  useEffect(() => {
    const t = setTimeout(() => {
      loadZones()
    }, 250)
    return () => clearTimeout(t)
  }, [loadZones])

  const openCreate = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setModalOpen(true)
  }

  const openEdit = (zone) => {
    setEditingId(zone.id)
    setForm(zoneToForm(zone))
    setModalOpen(true)
  }

  const closeModal = () => {
    if (saving) return
    setModalOpen(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!canEdit) return

    const payload = {
      region: form.region.trim(),
      neighborhood: form.neighborhood.trim(),
      price: Number(String(form.price).replace(',', '.')),
      notes: form.notes.trim() || null,
      active: form.active,
      cepStart: form.specialService ? null : form.cepStart,
      cepEnd: form.specialService ? null : form.cepEnd,
    }

    if (!payload.region || !payload.neighborhood) {
      showToast('Região e bairro são obrigatórios', 'error')
      return
    }
    if (!Number.isFinite(payload.price) || payload.price < 0) {
      showToast('Preço inválido', 'error')
      return
    }

    setSaving(true)
    try {
      const response = editingId
        ? await shippingZonesService.update(editingId, payload)
        : await shippingZonesService.create(payload)

      if (!response?.success) {
        showToast(response?.message || 'Não foi possível salvar', 'error')
        return
      }

      showToast(editingId ? 'Faixa atualizada' : 'Faixa criada', 'success')
      closeModal()
      await loadZones()
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || 'Erro ao salvar'
      showToast(msg, 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (zone) => {
    if (!canEdit || !zone.active) return
    if (!window.confirm(`Desativar a faixa "${zone.neighborhood}"?`)) return

    try {
      const response = await shippingZonesService.deactivate(zone.id)
      if (!response?.success) {
        showToast(response?.message || 'Erro ao desativar', 'error')
        return
      }
      showToast('Faixa desativada', 'success')
      await loadZones()
    } catch (error) {
      showToast(error?.response?.data?.message || 'Erro ao desativar', 'error')
    }
  }

  const handleQuoteTest = async () => {
    setTestLoading(true)
    setTestResult(null)
    try {
      const response = await shippingZonesService.quoteTest(testCep)
      if (!response?.success) {
        showToast(response?.message || 'Falha no teste', 'error')
        return
      }
      setTestResult(response.data)
    } catch (error) {
      showToast(error?.response?.data?.message || 'CEP inválido ou erro no teste', 'error')
    } finally {
      setTestLoading(false)
    }
  }

  const activeCount = useMemo(
    () => zones.filter((z) => z.active).length,
    [zones]
  )

  return (
    <div className="erp-settings-dashboard frete-cep-page">
      <button
        type="button"
        className="cockpit-settings-back-btn"
        onClick={() => navigate('/erp/configuracoes')}
      >
        <ArrowLeft size={14} /> Voltar às configurações
      </button>

      <div className="erp-settings-header">
        <div>
          <div className="frete-cep-title-row">
            <span className="cockpit-settings-icon-badge frete-cep-icon-badge">
              <Truck size={20} />
            </span>
            <h1 className="erp-settings-title">
              Frete por <span className="erp-settings-title-highlight">CEP</span>
            </h1>
          </div>
          <p className="erp-settings-subtitle">
            Tele-entrega local · {activeCount} ativa(s) · {zones.length} listada(s)
          </p>
        </div>
        <div className="erp-settings-header-actions">
          <button
            type="button"
            className="erp-settings-reload-btn"
            onClick={() => loadZones()}
            disabled={loading}
          >
            <RotateCcw size={16} className={loading ? 'cockpit-settings-spinner' : ''} />
            Atualizar
          </button>
          {canEdit && (
            <button type="button" className="frete-cep-primary-btn" onClick={openCreate}>
              <Plus size={16} /> Nova faixa
            </button>
          )}
        </div>
      </div>

      {!canEdit && (
        <div className="cockpit-settings-readonly-banner">
          Você tem permissão apenas de visualização. Peça UPDATE em Configurações para editar.
        </div>
      )}

      <div className="frete-cep-toolbar">
        <div className="frete-cep-search">
          <Search size={16} />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar região, bairro, CEP…"
          />
        </div>
        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value)}
          className="frete-cep-select"
        >
          <option value="">Todas as regiões</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <label className="frete-cep-check">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          Só ativas
        </label>
      </div>

      <div className="frete-cep-test-card">
        <div className="frete-cep-test-header">
          <MapPin size={16} />
          <strong>Testar CEP</strong>
        </div>
        <div className="frete-cep-test-row">
          <input
            type="text"
            inputMode="numeric"
            maxLength={9}
            value={testCep}
            onChange={(e) => setTestCep(formatCepInput(e.target.value))}
            placeholder="00000-000"
          />
          <button
            type="button"
            className="frete-cep-primary-btn"
            onClick={handleQuoteTest}
            disabled={testLoading}
          >
            {testLoading ? <Loader2 size={16} className="cockpit-settings-spinner" /> : 'Calcular'}
          </button>
        </div>
        {testResult && (
          <div className="frete-cep-test-result">
            {testResult.options?.length > 0 ? (
              <>
                <span>
                  {testResult.options[0].name} — {formatMoney(testResult.options[0].price)}
                </span>
                <small>
                  Região: {testResult.zone?.region} · Faixa{' '}
                  {formatCepDisplay(testResult.zone?.cepStart)}–{formatCepDisplay(testResult.zone?.cepEnd)}
                </small>
              </>
            ) : (
              <span className="frete-cep-test-empty">{testResult.message}</span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="erp-settings-loading">
          <Loader2 size={32} className="cockpit-settings-spinner" />
          <p>Carregando faixas…</p>
        </div>
      ) : zones.length === 0 ? (
        <div className="erp-settings-error">
          <p>Nenhuma faixa encontrada.</p>
        </div>
      ) : (
        <div className="frete-cep-table-wrap">
          <table className="frete-cep-table">
            <thead>
              <tr>
                <th>Região</th>
                <th>Bairro</th>
                <th>CEP</th>
                <th>Preço</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {zones.map((zone) => (
                <tr key={zone.id} className={!zone.active ? 'is-inactive' : ''}>
                  <td>{zone.region}</td>
                  <td>
                    <div className="frete-cep-cell-main">{zone.neighborhood}</div>
                    {zone.notes && <div className="frete-cep-cell-notes">{zone.notes}</div>}
                  </td>
                  <td>
                    {zone.cepStart && zone.cepEnd
                      ? `${formatCepDisplay(zone.cepStart)} – ${formatCepDisplay(zone.cepEnd)}`
                      : 'Serviço especial'}
                  </td>
                  <td>{formatMoney(zone.price)}</td>
                  <td>
                    <span className={`frete-cep-badge ${zone.active ? 'on' : 'off'}`}>
                      {zone.active ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="frete-cep-actions">
                    {canEdit && (
                      <>
                        <button type="button" title="Editar" onClick={() => openEdit(zone)}>
                          <Pencil size={16} />
                        </button>
                        {zone.active && (
                          <button
                            type="button"
                            title="Desativar"
                            className="danger"
                            onClick={() => handleDeactivate(zone)}
                          >
                            <Ban size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className="frete-cep-modal-overlay" onClick={closeModal}>
          <div
            className="frete-cep-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h2>{editingId ? 'Editar faixa' : 'Nova faixa'}</h2>
            <form onSubmit={handleSave} className="frete-cep-form">
              <label>
                Região *
                <input
                  value={form.region}
                  onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                  required
                />
              </label>
              <label>
                Bairro / trecho *
                <input
                  value={form.neighborhood}
                  onChange={(e) => setForm((f) => ({ ...f, neighborhood: e.target.value }))}
                  required
                />
              </label>
              <label>
                Preço (R$) *
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                  required
                />
              </label>
              <label className="frete-cep-check">
                <input
                  type="checkbox"
                  checked={form.specialService}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      specialService: e.target.checked,
                      cepStart: e.target.checked ? '' : f.cepStart,
                      cepEnd: e.target.checked ? '' : f.cepEnd,
                    }))
                  }
                />
                Serviço especial (sem faixa de CEP — não entra na cotação automática)
              </label>
              {!form.specialService && (
                <div className="frete-cep-form-row">
                  <label>
                    CEP inicial
                    <input
                      value={form.cepStart}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, cepStart: formatCepInput(e.target.value) }))
                      }
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </label>
                  <label>
                    CEP final
                    <input
                      value={form.cepEnd}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, cepEnd: formatCepInput(e.target.value) }))
                      }
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </label>
                </div>
              )}
              <label>
                Observações
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </label>
              <label className="frete-cep-check">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                Ativa
              </label>
              <div className="frete-cep-modal-actions">
                <button type="button" className="frete-cep-ghost-btn" onClick={closeModal} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="frete-cep-primary-btn" disabled={saving}>
                  {saving ? <Loader2 size={16} className="cockpit-settings-spinner" /> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
      />
    </div>
  )
}

export default FretePorCepPage
